import { Fragment, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { MkAvatar } from "./MkAvatar";
import type { Brand, NavItem, Personal } from "./types";

interface Props {
    brand?: Brand;
    personal?: Personal;
    items: NavItem[];
    active?: string;
    collapsed?: boolean;
    onSelect(id: string, item: NavItem): void;
    onToggle?(): void;
    onLogout?(): void;
    logoutLabel?: string;
    switcher?: ReactNode;
    /** the application the reader is in: entering another one opens its branch */
    section?: string;
}

function initial(item: NavItem): string {
    return (item.label || "?").trim().charAt(0).toUpperCase();
}

export function MkSidebar({ brand, personal, items, active, collapsed,
                            onSelect, onToggle, onLogout, logoutLabel = "Log out", switcher, section }: Props) {
    const { t } = useTranslation();
    const [open, setOpen] = useState<Record<string, boolean>>({});
    const nav = useRef<HTMLElement>(null);

    // Entering another application opens its branch as the menu first drew it,
    // without drawing the menu again: the reader's place in it is kept.
    useEffect(() => {
        setOpen({});
    }, [section]);

    // The open application stays in sight. The menu scrolls only as far as it
    // must to show the application and its pages, never back to the top.
    useEffect(() => {
        const frame = requestAnimationFrame(() => {
            const box = nav.current;
            if (!box) {
                return;
            }
            const current = box.querySelector<HTMLElement>(".mk-sidebar-children-current");
            const head = (current?.previousElementSibling as HTMLElement | null)
                ?? box.querySelector<HTMLElement>(".mk-sidebar-item-active");
            if (!head) {
                return;
            }
            const frameTop = box.getBoundingClientRect().top;
            const top = head.getBoundingClientRect().top - frameTop + box.scrollTop - 8;
            const last = current ?? head;
            const bottom = last.getBoundingClientRect().bottom - frameTop + box.scrollTop + 8;
            if (top < box.scrollTop) {
                box.scrollTo({ top, behavior: "smooth" });
            } else if (bottom > box.scrollTop + box.clientHeight) {
                box.scrollTo({ top: Math.min(top, bottom - box.clientHeight), behavior: "smooth" });
            }
        });
        return () => cancelAnimationFrame(frame);
    // the items arrive after the first draw: the place is found again once they do
    }, [active, section, items]);

    const isActive = (id?: string) =>
        id != null && active != null && String(id) === String(active);

    const hasChildren = (item: NavItem) => Boolean(item.children?.length);
    const isOpen = (item: NavItem) => open[item.id] ?? Boolean(item.defaultOpen ?? true);

    function isBranchActive(item: NavItem): boolean {
        if (!hasChildren(item)) {
            return false;
        }
        return (item.children ?? []).some(
            (child) => isActive(child.id) || isBranchActive(child));
    }

    function branchClass(item: NavItem): string {
        if (!hasChildren(item)) {
            return "";
        }
        const classes: string[] = [];
        if (isOpen(item)) {
            classes.push("mk-sidebar-item-open");
        }
        if (isBranchActive(item)) {
            classes.push("mk-sidebar-item-branch");
        }
        return classes.length ? ` ${classes.join(" ")}` : "";
    }

    function toggle(item: NavItem) {
        setOpen((current) => ({ ...current, [item.id]: !isOpen(item) }));
    }

    function click(item: NavItem) {
        if (hasChildren(item) && !collapsed) {
            if (!isOpen(item)) {
                setOpen((current) => ({ ...current, [item.id]: true }));
                return;
            }
            if (!item.selectable) {
                toggle(item);
                return;
            }
        }
        onSelect(item.id, item);
    }

    function renderItems(list: NavItem[], depth: number) {
        return list.map((item, index) => {
            if (item.section) {
                return (
                    <div className="mk-sidebar-section" key={item.id || `s${index}`}>
                        {item.section}
                    </div>
                );
            }
            return (
                <Fragment key={item.id || `s${index}`}>
                    <button
                        className={(depth ? "mk-sidebar-child" : "mk-sidebar-item")
                            + (isActive(item.id) ? " mk-sidebar-item-active" : "")
                            + branchClass(item)}
                        title={item.label}
                        onClick={() => click(item)}
                    >
                        {depth && !item.imgSrc && !item.icon ? (
                            <>
                                <span className="mk-sidebar-dot" />
                                <span className="mk-sidebar-initial">{initial(item)}</span>
                            </>
                        ) : (
                            <span className="mk-sidebar-item-icon">
                                {item.imgSrc
                                    ? <img src={item.imgSrc} alt="" />
                                    : item.tile
                                        ? <span className="mk-sidebar-tile"><i className={`fa ${item.icon || "fa-circle-o"}`} /></span>
                                        : <i className={`fa ${item.icon || "fa-circle-o"}`} />}
                            </span>
                        )}
                        <span className="mk-sidebar-item-label">{item.label}</span>
                        {item.badge ? (
                            <span className="mk-sidebar-badge">{item.badge}</span>
                        ) : null}
                        {hasChildren(item) && (
                            <span
                                className="mk-sidebar-caretbtn"
                                role="button"
                                tabIndex={0}
                                title={isOpen(item) ? t("mk.collapse") : t("mk.expand")}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    toggle(item);
                                }}
                            >
                                <i className={`fa fa-angle-down mk-sidebar-caret${
                                    isOpen(item) ? " mk-open" : ""}`} />
                            </span>
                        )}
                    </button>
                    {hasChildren(item) && isOpen(item) && (
                        <div className={`mk-sidebar-children${
                            isBranchActive(item) ? " mk-sidebar-children-current" : ""}`}>
                            {renderItems(item.children ?? [], depth + 1)}
                        </div>
                    )}
                </Fragment>
            );
        });
    }

    return (
        <aside className={`mk-sidebar${collapsed ? " mk-sidebar-icon" : ""}`}>
            <nav className="mk-sidebar-nav" ref={nav}>
                {brand && (
                    <div className="mk-sidebar-brandbar">
                        <button
                            className={`mk-sidebar-item mk-sidebar-brand${
                                isActive(brand.id) ? " mk-sidebar-item-active" : ""}`}
                            title={brand.label}
                            onClick={() => brand.onClick?.()}
                        >
                            <span className="mk-sidebar-item-icon mk-sidebar-brand-icon">
                                {brand.logo
                                    ? <img src={brand.logo} alt="" />
                                    : <i className={`fa ${brand.icon || "fa-cube"}`} />}
                            </span>
                            <span className="mk-sidebar-item-label">{brand.label || "Mishkat"}</span>
                        </button>
                        <button className="mk-sidebar-toggle" onClick={() => onToggle?.()}
                                title={collapsed ? t("mk.expandSidebar") : t("mk.collapseSidebar")}>
                            <i className="fa fa-angle-left" />
                        </button>
                    </div>
                )}

                {switcher}

                {renderItems(items, 0)}
            </nav>

            {(personal || onLogout) && (
                <div className="mk-sidebar-footer">
                    {personal && (
                        <button className="mk-sidebar-user" title={personal.name}
                                onClick={() => personal.onClick?.()}>
                            <MkAvatar size="sm" src={personal.avatarUrl}
                                      name={personal.name} status={personal.status} />
                            <span className="mk-sidebar-user-name">{personal.name}</span>
                        </button>
                    )}
                    {onLogout && (
                        <button className="mk-sidebar-logout" title={logoutLabel}
                                onClick={() => onLogout()}>
                            <i className="fa fa-sign-out" />
                        </button>
                    )}
                </div>
            )}
        </aside>
    );
}
